import Component from '@ember/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type CurrentUser from 'donor-elf-web/services/current-user';
import type { EmberChangeset } from 'ember-changeset';

type Option = {
  val: string;
  label: string;
  searchLabel?: string; // Optional.  If supplied, the search will be based on this property instead of label
};

interface ComponentArgs {
  changeset: EmberChangeset;
  property: string;
  options: Option[];
  label?: string;
  placeholder?: string;

  // Whether to close the dropdown when filter textbox has focus and enter is pressed.  Default false
  closeDropdownAfterEnter?: boolean;

  maximumOptionsInDropdown?: number; // default 20
  dropdownContentClass?: string; // 'tw-z-1100' can be used when the component is in a <Modal /> component
  onChange?: (selectedVals: string[]) => void;
}

export default class FormGroupSelectMultipleComponent extends Component<ComponentArgs> {
  @service declare currentUser: CurrentUser;

  @tracked declare changeset: EmberChangeset;
  @tracked declare property: string;
  @tracked options: Option[] = [];
  @tracked label?: string | undefined;
  @tracked closeDropdownAfterEnter = false;
  @tracked maximumOptionsInDropdown = 20;
  @tracked selectedVals: string[] = [];
  @tracked searchVal = '';
  @tracked searchSelectedIndex = 0;

  onChange?: (selectedVals: string[]) => void;

  classNames = ['form-group'];
  dropdown?: any;

  get optionsByVal() {
    const byVal: Record<string, Option> = {};

    this.options.forEach((option) => {
      byVal[option.val] = option;
    });

    return byVal;
  }

  get selectedOptions() {
    const optionsByVal = this.optionsByVal;

    return (
      this.selectedVals.map((val) => {
        const option = optionsByVal[val];

        if (option) {
          return option;
        }

        if (val === this.currentUser.id) {
          return { val, label: 'Me' };
        }

        return { val, label: val };
      }) ?? []
    );
  }

  get notSelectedOptions() {
    return (
      this.options.filter((option) => {
        return this.selectedVals?.indexOf(option.val) === -1;
      }) ?? []
    );
  }

  get filteredNotSelectedOptions() {
    if (this.searchVal == null || this.searchVal.length < 2) {
      return this.notSelectedOptions;
    }

    const scrubbedSearch = this.searchVal.trim().toLowerCase();

    return this.notSelectedOptions.filter((option) => {
      if (option.searchLabel == null) {
        option.searchLabel = option.label.toLowerCase();
      }

      return option.searchLabel.indexOf(scrubbedSearch) >= 0;
    });
  }

  get dropdownOptions() {
    return this.filteredNotSelectedOptions.slice(
      0,
      this.maximumOptionsInDropdown,
    );
  }

  get moreDropdownOptionsToShow() {
    return (
      this.filteredNotSelectedOptions.length > this.maximumOptionsInDropdown
    );
  }

  didReceiveAttrs() {
    this._super(...arguments);

    if (this.changeset.get(this.property) == null) {
      this.changeset.set(this.property, []);
    }

    this.selectedVals = this.changeset.get(this.property);
  }

  handleDropdownOpened = (
    dropdown: HTMLElement,
    e: Event & { target: HTMLElement },
  ) => {
    const { target } = e;

    this.dropdown = dropdown;

    if (target?.className?.indexOf('form-group-select-multiple-delete') >= 0) {
      return false;
    }

    this.searchVal = '';
    this.searchSelectedIndex = 0;
  };

  focusOnInput(el: HTMLElement) {
    el.querySelector('input')?.focus();
  }

  removeAtIndex = (index: number) => {
    this.selectedVals = [
      ...this.selectedVals.slice(0, index),
      ...this.selectedVals.slice(index + 1),
    ];

    this.changeset.set(this.property, this.selectedVals);
    this.onChange?.(this.selectedVals);
  };

  selectOption = (option: Option) => {
    this.selectedVals = [...this.selectedVals, option.val];
    this.changeset.set(this.property, this.selectedVals);

    this.dropdown.actions.close();
    this.onChange?.(this.selectedVals);
  };

  handleSearchInput = (value: string) => {
    this.searchVal = value;
    this.searchSelectedIndex = 0;
  };

  handleSearchUpArrow = (e: Event) => {
    if (this.searchSelectedIndex > 0) {
      this.searchSelectedIndex--;
    }

    e.preventDefault();
  };

  handleSearchDownArrow = (e: Event) => {
    if (this.searchSelectedIndex < this.filteredNotSelectedOptions.length - 1) {
      this.searchSelectedIndex++;
    }
    e.preventDefault();
  };

  handleSearchTab = (e: Event) => {
    e.preventDefault();
    this.dropdown.actions.close();
  };

  handleSearchEnter = () => {
    let option = this.filteredNotSelectedOptions[this.searchSelectedIndex];

    if (option) {
      this.selectedVals = [...this.selectedVals, option.val];
      this.changeset.set(this.property, this.selectedVals);

      this.searchVal = '';

      if (this.closeDropdownAfterEnter) {
        this.dropdown.actions.close();
      } else {
        this.dropdown.actions.reposition();
      }

      this.onChange?.(this.selectedVals);
    }
  };
}
